import React, { useState, useRef, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Card, Badge, Row, Col, Form, InputGroup } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle, faUpload, faCircleX, faSearch, faTimes } from '@fortawesome/pro-solid-svg-icons';

import { addLibraryPage, removeAvailablePage } from '../store/actions';
import { deletePdfTemplate } from '../api';
import { displayToast } from 'bundles/connect/helpers/display_toast';
import badgeClass from '../shared/document_badge';
import { getPageKey } from './page_key_helper';
import { Button } from 'components/ui/button';
import DeletePdfModal from './delete_pdf_modal';
import { type DocumentDefaultsType, type Page } from '../types';

interface AvailablePagesProps {
  addLibraryPage: (id: number, templateType?: string) => void;
  removeAvailablePage: (id: number, templateType: string) => void;
  basePath: string;
  locked?: boolean;
  pages: Page[];
  onUploadPdf?: () => void;
}

export function AvailablePages({
  addLibraryPage,
  removeAvailablePage,
  basePath,
  onUploadPdf,
  pages,
  locked
}: AvailablePagesProps) {
  const [deleteModalPage, setDeleteModalPage] = useState<Page | null>(null);
  const [searchText, setSearchText] = useState('');
  const [createdByFilter, setCreatedByFilter] = useState('');
  const cardRef = useRef<HTMLDivElement>(null);
  const prevPagesLengthRef = useRef(pages.length);

  // scroll to bottom when pdf pages are uploaded
  useEffect(() => {
    if (pages.length > prevPagesLengthRef.current && cardRef.current) {
      cardRef.current.scrollTop = cardRef.current.scrollHeight;
    }
    prevPagesLengthRef.current = pages.length;
  }, [pages.length]);

  const cardOverflowCss: React.CSSProperties = {
    maxHeight: '475px',
    overflowY: 'auto'
  };

  const handleDeleteClick = (page: Page) => {
    setDeleteModalPage(page);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModalPage) return;

    try {
      await deletePdfTemplate(basePath, deleteModalPage.id);
      removeAvailablePage(deleteModalPage.id, deleteModalPage.template_type!);
      displayToast('PDF template deleted successfully', 'success');
    } catch {
      displayToast('Failed to delete PDF template', 'error');
    }
  };

  const createdByOptions = useMemo(() => Array.from(
    new Set(pages.map(page => page.createdByName).filter((name): name is string => Boolean(name)))
  ).sort((a, b) => a.localeCompare(b)), [pages]);

  useEffect(() => {
    if (createdByFilter && !createdByOptions.includes(createdByFilter)) {
      setCreatedByFilter('');
    }
  }, [createdByFilter, createdByOptions]);

  const pagesForDisplay = useMemo(() => {
    const normalizedSearchText = searchText.trim().toLowerCase();

    return pages.filter((page) => {
      const pageName = page.name?.toLowerCase() || '';
      const matchesSearch = normalizedSearchText.length === 0 || pageName.includes(normalizedSearchText);
      const matchesCreatedBy = createdByFilter.length === 0 || page.createdByName === createdByFilter;

      return matchesSearch && matchesCreatedBy;
    });
  }, [pages, searchText, createdByFilter]);

  return (
    <React.Fragment>
      {!locked && (
        <div>
          <div className="my-3 d-flex align-items-center">
            <div className="flex-grow-1">
              <strong className="fw-semibold">Available Pages</strong>
            </div>
            <Button icon={faUpload} variant="secondary" onClick={onUploadPdf}>
              Upload A PDF
            </Button>
          </div>
          <Row className="g-2 mb-3">
            <Col lg={3} md={9} xs={12}>
              <InputGroup>
                <InputGroup.Text className="text-primary">
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search"
                  type="text"
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                />
                <button
                  aria-label="Clear search"
                  className="btn btn-outline-secondary"
                  disabled={!searchText}
                  type="button"
                  onClick={() => setSearchText('')}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </InputGroup>
            </Col>
            <Col lg={2} md={3} xs={12}>
              <Form.Select
                aria-label="Created By"
                value={createdByFilter}
                onChange={e => setCreatedByFilter(e.target.value)}
              >
                <option value="">Created By</option>
                {createdByOptions.map(createdByName => (
                  <option key={createdByName} value={createdByName}>
                    {createdByName}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
          {pagesForDisplay.length === 0 && (
            <div className="text-muted py-4 text-center">No results</div>
          )}
          {pagesForDisplay.length !== 0 && (
            <Card className="overflow-auto" ref={cardRef} style={cardOverflowCss}>
              <ul className="list-group list-group-flush">
                {pagesForDisplay.map(page => (
                  <li className="list-group-item" key={getPageKey(page)}>
                    <Row className="align-items-center w-100">
                      <Col xs={3}>
                        {page.name}
                      </Col>
                      <Col xs={6}>
                        {page.user_label && (
                          <Badge className={`${badgeClass('user_label')} m-1`}>{page.user_label}</Badge>
                        )}
                        {page.size && (
                          <Badge bg="muted" className={`${badgeClass('size', page.size)} m-1`}>{page.size}</Badge>
                        )}
                      </Col>
                      <Col className="text-end" xs={3}>
                        {
                          page.template_type === 'Docs::PdfPageTemplate' && (
                            <FontAwesomeIcon
                              className="text-danger clickable"
                              icon={faCircleX}
                              name="remove-pdf"
                              onClick={() => handleDeleteClick(page)}
                            />
                          )
                        }
                        <FontAwesomeIcon
                          className="text-success clickable ms-4"
                          icon={faPlusCircle}
                          name="add-page"
                          onClick={() => addLibraryPage(page.id, page.template_type)}
                        />
                      </Col>
                    </Row>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      )}
      {deleteModalPage && (
        <DeletePdfModal
          basePath={basePath}
          pdfId={deleteModalPage.id}
          show={true}
          onClose={() => setDeleteModalPage(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </React.Fragment>
  );
}

const mapStateToProps = ({ availablePages, formData }: { availablePages: Page[]; formData: DocumentDefaultsType }) => {
  const documentTemplateAvailablePageIds = formData.availablePageTemplateTreeIds || [];

  // Filter out pages that are already in the library
  // Compare both id AND template_type to handle ID collisions between different types
  const pages = availablePages.filter((page : Page) => {
    return !documentTemplateAvailablePageIds.some(p => p.id === page.id && p.template_type === page.template_type
    );
  });

  return { pages };
};

export default connect(mapStateToProps, { addLibraryPage, removeAvailablePage })(AvailablePages);
